import React, { useState, useEffect } from 'react';
import { Download, Share2, Copy, Loader, Eye, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import AdvancedQRGeneratorService from '../../../services/advancedQRGeneratorService';
import { downloadBlob, downloadDataUrl, downloadHref, downloadText } from '../../../utils/download';

const ExportControls = ({ 
  qrData, 
  qrConfig, 
  onSave,
  isGenerating = false 
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [trackingValidation, setTrackingValidation] = useState(null);

  React.useEffect(() => {
    // Validate QR uses trackable URL
    if ((qrData?.trackableUrl || qrData?.trackableContent) && (qrData?.originalContent || qrData?.directUrl)) {
      setTrackingValidation({
        isValid: true,
        message: 'QR code uses trackable URL - analytics enabled',
        trackingUrl: qrData?.trackableUrl || qrData?.trackableContent,
        directUrl: qrData?.originalContent || qrData?.directUrl
      });
    } else if ((qrData?.dataUrl || qrData?.qrDataUrl || qrData?.svgData) && !(qrData?.trackableUrl || qrData?.trackableContent)) {
      setTrackingValidation({
        isValid: false,
        message: 'Warning: QR code may bypass tracking system',
        recommendation: 'Save QR code to ensure proper analytics tracking'
      });
    }
  }, [qrData]);

  const handleDownload = async (format) => {
    // IMPORTANT: Preview QRs are not persisted, so their short codes won't exist in the DB.
    // Downloading/printing them guarantees "QR code not found" on scan.
    if (qrData?.isPreview || (qrData?.shortCode && String(qrData.shortCode).startsWith('preview_'))) {
      setExportError('Dit is een preview QR. Klik eerst op "Save QR Code" voordat je PNG/SVG downloadt (anders kan tracking nooit werken).');
      return;
    }

    if (!qrData?.dataUrl && !qrData?.qrDataUrl && !qrData?.svgData && !qrData?.canvas && !qrData?.qrCodeInstance) {
      setExportError('No QR code data available for download');
      return;
    }

    try {
      setIsExporting(true);
      setExportError('');

      // Ensure we're using trackable URL for final export
      let finalQRData = qrData;
      
      if (!(qrData?.trackableUrl || qrData?.trackableContent) && qrConfig?.shortCode) {
        console.log('CRITICAL: Regenerating QR with trackable URL for proper analytics');
        finalQRData = await AdvancedQRGeneratorService?.generateQRCodeWithTracking(
          qrConfig?.content || qrConfig?.data,
          null, // qrCodeId
          qrConfig?.shortCode,
          {
            ...qrConfig,
            size: 512,
            format
          }
        );
        
        if (!finalQRData?.success) {
          throw new Error(finalQRData?.error || 'Failed to regenerate QR with tracking');
        }
      }

      const timestamp = Date.now();
      const filename = `qr-code-${timestamp}.${format === 'svg' ? 'svg' : 'png'}`;

      // IMPORTANT: keep downloads as synchronous as possible (especially for iOS/Safari),
      // otherwise the browser may block the download if it happens after awaits.
      const qrInstance = finalQRData?.qrCodeInstance || qrData?.qrCodeInstance;
      const dataUrl = finalQRData?.dataUrl || finalQRData?.qrDataUrl || qrData?.dataUrl || qrData?.qrDataUrl;

      if (format === 'svg') {
        const svgData = finalQRData?.svgData || qrData?.svgData;
        if (svgData) {
          // Synchronous path (best): create blob from SVG string.
          downloadText(svgData, filename, 'image/svg+xml;charset=utf-8');
        } else if (dataUrl) {
          // Synchronous path: download the SVG data URL directly.
          downloadHref(dataUrl, filename);
        } else if (qrInstance?.getRawData) {
          // Fallback (async)
          const blob = await qrInstance.getRawData('svg');
          if (!blob) throw new Error('QR export returned no data');
          downloadBlob(blob, filename);
        } else {
          throw new Error('No SVG data available for download');
        }
      } else {
        const canvas = finalQRData?.canvas || qrData?.canvas;
        if (canvas?.toDataURL) {
          // Synchronous path: prefer dataURL (avoids async toBlob)
          const pngDataUrl = canvas.toDataURL('image/png');
          downloadHref(pngDataUrl, filename);
        } else if (dataUrl) {
          // Synchronous: dataUrl from generator
          downloadHref(dataUrl, filename);
        } else if (qrInstance?.getRawData) {
          // Fallback (async)
          const blob = await qrInstance.getRawData('png');
          if (!blob) throw new Error('QR export returned no data');
          downloadBlob(blob, filename);
        } else {
          throw new Error('No PNG data available for download');
        }
      }

      // Update tracking validation
      if (finalQRData?.trackableUrl || finalQRData?.trackableContent) {
        setTrackingValidation({
          isValid: true,
          message: 'QR downloaded with trackable URL - analytics enabled',
          trackingUrl: finalQRData?.trackableUrl || finalQRData?.trackableContent
        });
      }

    } catch (error) {
      console.error('Download failed:', error);
      setExportError(`Download failed: ${error?.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      if (!qrData?.trackableUrl && !qrData?.trackableContent && !qrData?.dataUrl && !qrData?.qrDataUrl) {
        setExportError('No QR code data available to copy');
        return;
      }

      // Priority: trackable URL > QR data URL
      const urlToCopy = qrData?.trackableUrl || qrData?.trackableContent || qrData?.dataUrl || qrData?.qrDataUrl;
      
      await navigator.clipboard?.writeText(urlToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);

      // Show appropriate message based on what was copied
      if (qrData?.trackableUrl || qrData?.trackableContent) {
        setTrackingValidation({
          isValid: true,
          message: 'Trackable URL copied - analytics enabled',
          trackingUrl: qrData?.trackableUrl || qrData?.trackableContent
        });
      }

    } catch (error) {
      setExportError('Failed to copy to clipboard');
      console.error('Copy failed:', error);
    }
  };

  const handlePreviewQR = () => {
    if (qrData?.trackableUrl || qrData?.trackableContent) {
      // Open trackable URL to test the flow
      window.open(qrData?.trackableUrl || qrData?.trackableContent, '_blank');
      setTrackingValidation({
        isValid: true,
        message: 'Opened trackable URL - analytics will be recorded',
        trackingUrl: qrData?.trackableUrl || qrData?.trackableContent
      });
    } else if (qrData?.originalContent || qrConfig?.content) {
      // Fallback to direct content
      window.open(qrData?.originalContent || qrConfig?.content, '_blank');
      setTrackingValidation({
        isValid: false,
        message: 'Opened direct URL - analytics may not work',
        recommendation: 'Save QR code first to enable tracking'
      });
    } else {
      setExportError('No URL available for preview');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Export & Share</h3>
      
      {/* Tracking Status Display */}
      {trackingValidation && (
        <div className={`mb-4 p-3 rounded-lg border ${
          trackingValidation?.isValid 
            ? 'bg-green-50 border-green-200' :'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-start gap-2">
            {trackingValidation?.isValid ? (
              <Eye className="w-4 h-4 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            )}
            <div>
              <p className={`text-sm font-medium ${
                trackingValidation?.isValid ? 'text-green-800' : 'text-amber-800'
              }`}>
                {trackingValidation?.message}
              </p>
              {trackingValidation?.trackingUrl && (
                <p className="text-xs text-gray-600 mt-1 font-mono">
                  Tracking: {trackingValidation?.trackingUrl}
                </p>
              )}
              {trackingValidation?.directUrl && (
                <p className="text-xs text-gray-600 mt-1 font-mono">
                  Destination: {trackingValidation?.directUrl}
                </p>
              )}
              {trackingValidation?.recommendation && (
                <p className="text-xs text-amber-700 mt-1">
                  {trackingValidation?.recommendation}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {exportError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{exportError}</p>
        </div>
      )}

      {/* Export Actions */}
      <div className="grid grid-cols-2 gap-3">
        {/* Download PNG */}
        <Button
          type="button"
          onClick={() => handleDownload('png')}
          disabled={
            isExporting ||
            isGenerating ||
            qrData?.isPreview ||
            (qrData?.shortCode && String(qrData.shortCode).startsWith('preview_')) ||
            (!qrData?.dataUrl && !qrData?.qrDataUrl && !qrData?.svgData && !qrData?.canvas && !qrData?.qrCodeInstance)
          }
          variant="outline"
          className="flex items-center gap-2"
        >
          {isExporting ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          PNG
        </Button>

        {/* Download SVG */}
        <Button
          type="button"
          onClick={() => handleDownload('svg')}
          disabled={
            isExporting ||
            isGenerating ||
            qrData?.isPreview ||
            (qrData?.shortCode && String(qrData.shortCode).startsWith('preview_')) ||
            (!qrData?.dataUrl && !qrData?.qrDataUrl && !qrData?.svgData && !qrData?.canvas && !qrData?.qrCodeInstance)
          }
          variant="outline"
          className="flex items-center gap-2"
        >
          {isExporting ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          SVG
        </Button>

        {/* Copy URL */}
        <Button
          type="button"
          onClick={handleCopyToClipboard}
          disabled={isGenerating || (!qrData?.trackableUrl && !qrData?.trackableContent && !qrData?.dataUrl && !qrData?.qrDataUrl)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Copy className="w-4 h-4" />
          {copySuccess ? 'Copied!' : 'Copy URL'}
        </Button>

        {/* Preview */}
        <Button
          type="button"
          onClick={handlePreviewQR}
          disabled={isGenerating || (!qrData?.trackableUrl && !qrData?.trackableContent && !qrData?.originalContent && !qrConfig?.content)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          Preview
        </Button>

        {/* Save QR Code (Full Width) */}
        <div className="col-span-2">
          <Button
            type="button"
            onClick={onSave}
            disabled={isGenerating || !qrData}
            className="w-full flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4" />
                Save QR Code
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Additional Info */}
      <div className="mt-4 text-xs text-gray-500 border-t pt-3">
        <p>💡 Saving the QR code enables trackable URLs for analytics</p>
        {qrData?.shortCode && (
          <p className="mt-1">Short code: <span className="font-mono">{qrData?.shortCode}</span></p>
        )}
      </div>
    </div>
  );
};

export default ExportControls;